import { lazy, Suspense } from 'react';
import {
  Text,
  Badge,
  Button,
  Icon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import type { AutomationInputsProps, InputComponentType } from './types';
import type { V1ValueType } from '@/generated/hooks/grimoire/models';

// Lazy load all input components for better code splitting
// Each input is imported from its own file to enable true code splitting
const TextInput = lazy(() =>
  import('./components/TextInput').then(m => ({ default: m.TextInput })),
);
const NumberInput = lazy(() =>
  import('./components/NumberInput').then(m => ({ default: m.NumberInput })),
);
const BooleanInput = lazy(() =>
  import('./components/BooleanInput').then(m => ({ default: m.BooleanInput })),
);
const DateInput = lazy(() =>
  import('./components/DateInput').then(m => ({ default: m.DateInput })),
);
const DateTimeInput = lazy(() =>
  import('./components/DateTimeInput').then(m => ({
    default: m.DateTimeInput,
  })),
);
const TimeInput = lazy(() =>
  import('./components/TimeInput').then(m => ({ default: m.TimeInput })),
);
const FileInput = lazy(() =>
  import('./components/file-upload/FileInput').then(m => ({
    default: m.FileInput,
  })),
);
const EnumInput = lazy(() =>
  import('./components/EnumInput').then(m => ({ default: m.EnumInput })),
);
const ComplexInput = lazy(() =>
  import('./components/ComplexInput').then(m => ({ default: m.ComplexInput })),
);
const ListInput = lazy(() =>
  import('./components/ListInput').then(m => ({ default: m.ListInput })),
);
import {
  getEnumMembers,
  getComplexTypeHint,
  isFileListType,
} from './utils/typeDetection';

/** Stable empty array reference to avoid re-renders when no default input keys are provided. */
const EMPTY_DEFAULT_INPUT_KEYS: string[] = [];
import { listToCommonValue, toCommonValue } from './utils/valueConversion';
import { processInputSpec } from './utils/inputProcessing';

/**
 * Main component for rendering automation input fields
 *
 * Renders a collection of dynamically-typed input fields based on automation input
 * specifications. Automatically selects the appropriate input component type (text,
 * number, date, file, etc.) based on the V1ValueType definition.
 *
 * @param props - Component props
 * @returns JSX element containing the input fields, or null if no inputs
 */
export function AutomationInputs({
  inputSpecs,
  values,
  errors,
  onValuesChange,
  isReadonly = false,
  defaultInputKeys = EMPTY_DEFAULT_INPUT_KEYS,
  onToggleDefault,
  fileHistoryMap,
  onSelectHistoryFile,
}: AutomationInputsProps) {
  const { t } = useTranslation('automation-inputs');

  /**
   * Handles changes to input values by converting display values to Commonv1Value
   * format and notifying the parent component
   *
   * @param name - The field name that changed
   * @param value - The new display value (string or boolean)
   * @param inputType - The type of input component (determines conversion logic)
   */
  const handleChange = (
    name: string,
    value: string | boolean,
    inputType: string,
  ) => {
    // Convert the display value to Commonv1Value
    const commonValue = toCommonValue(value, inputType);

    // Update values and notify parent
    const newValues = { ...values, [name]: commonValue };
    onValuesChange?.(newValues);
  };

  if (!inputSpecs || inputSpecs.length === 0) {
    return null;
  }

  /**
   * Renders a self-contained editor for a leaf type (a scalar or an enum).
   *
   * Split out from the row-level renderer so `List[T]` element rows can reuse
   * it: an element gets exactly the control its own type would get at the top
   * level, which is what keeps list handling generic instead of needing a rule
   * per element type.
   *
   * @param args.name - Identifier for the field's id/name/test id
   * @param args.componentType - Which leaf editor to render
   * @param args.type - The leaf's unwrapped V1ValueType (enum members come from here)
   * @param args.value - Current display value
   * @param args.onChange - Receives the new display value
   * @param args.error - Optional validation error message
   * @param args.required - Whether the field is required
   * @param args.label - Label for controls that need one (checkboxes)
   * @returns The rendered editor, or null when the type has no leaf editor
   */
  const renderLeafEditor = ({
    name,
    componentType,
    type,
    value,
    onChange,
    error,
    required,
    label,
  }: {
    name: string;
    componentType: InputComponentType;
    type: V1ValueType;
    value: string | boolean;
    onChange: (value: string | boolean) => void;
    error?: string;
    required?: boolean;
    label?: string;
  }) => {
    switch (componentType) {
      case 'text':
        return (
          <Suspense fallback={null}>
            <TextInput
              name={name}
              value={value as string}
              onChange={onChange}
              error={error}
              required={required}
              disabled={isReadonly}
              placeholder={t('placeholders.enterText', {
                field: 'Text',
              })}
            />
          </Suspense>
        );

      case 'number':
        return (
          <Suspense fallback={null}>
            <NumberInput
              name={name}
              value={value as string}
              onChange={onChange}
              error={error}
              required={required}
              disabled={isReadonly}
              placeholder={t('placeholders.enterNumber', {
                field: 'Number',
              })}
            />
          </Suspense>
        );

      case 'boolean':
        return (
          <Suspense fallback={null}>
            <BooleanInput
              name={name}
              // List elements arrive as display strings, top-level checkboxes as
              // real booleans; both mean the same thing here.
              checked={value === true || value === 'true'}
              onChange={onChange}
              error={error}
              required={required}
              label={label ?? name}
              disabled={isReadonly}
            />
          </Suspense>
        );

      case 'date':
        return (
          <Suspense fallback={null}>
            <DateInput
              name={name}
              value={value as string}
              onChange={onChange}
              error={error}
              required={required}
              disabled={isReadonly}
            />
          </Suspense>
        );

      case 'datetime':
        return (
          <Suspense fallback={null}>
            <DateTimeInput
              name={name}
              value={value as string}
              onChange={onChange}
              error={error}
              required={required}
              disabled={isReadonly}
            />
          </Suspense>
        );

      case 'time':
        return (
          <Suspense fallback={null}>
            <TimeInput
              name={name}
              value={value as string}
              onChange={onChange}
              error={error}
              required={required}
              disabled={isReadonly}
            />
          </Suspense>
        );

      case 'enum':
        return (
          <Suspense fallback={null}>
            <EnumInput
              name={name}
              value={value as string}
              options={getEnumMembers(type)}
              onChange={onChange}
              error={error}
              required={required}
              disabled={isReadonly}
            />
          </Suspense>
        );

      default:
        return null;
    }
  };

  /**
   * Renders the editor for one input spec row.
   *
   * Handles the composite and non-editable component types itself and defers
   * every leaf type to `renderLeafEditor`.
   *
   * @param spec - The input specification
   * @param processed - The spec's resolved type/value information
   * @param isDefault - Whether this input is marked as a default input
   * @returns The rendered input component, or null if type is unknown
   */
  const renderInputComponent = (
    spec: (typeof inputSpecs)[0],
    processed: ReturnType<typeof processInputSpec>,
    isDefault: boolean,
  ) => {
    const {
      componentType,
      displayValue,
      error,
      required,
      baseType,
      elementType,
      elementComponentType,
      listItems,
    } = processed;

    switch (componentType) {
      case 'file': {
        const isMultiple = isFileListType(baseType);
        const fileHistory =
          !isMultiple && fileHistoryMap?.[spec.name]?.length
            ? fileHistoryMap[spec.name]
            : undefined;
        return (
          <Suspense fallback={null}>
            <FileInput
              name={spec.name}
              value={displayValue as string}
              onChange={val =>
                handleChange(spec.name, val, isMultiple ? 'fileList' : 'file')
              }
              error={error}
              required={required}
              multiple={isMultiple}
              disabled={isReadonly}
              fileHistory={fileHistory}
              onSelectHistoryFile={value =>
                onSelectHistoryFile?.(spec.name, value)
              }
              isDefault={isDefault}
            />
          </Suspense>
        );
      }

      case 'list': {
        // Guaranteed by `resolveComponentType`: 'list' is only chosen once the
        // element type resolves to a leaf editor.
        if (!elementType || !elementComponentType) return null;
        return (
          <Suspense fallback={null}>
            <ListInput
              name={spec.name}
              items={listItems}
              onChange={items =>
                onValuesChange?.({
                  ...values,
                  [spec.name]: listToCommonValue(items, elementType),
                })
              }
              renderElement={({ index, value, onChange }) =>
                renderLeafEditor({
                  name: `${spec.name}-${index}`,
                  componentType: elementComponentType,
                  type: elementType,
                  value,
                  onChange: next => onChange(String(next)),
                  label: `${spec.name} ${index + 1}`,
                })
              }
              disabled={isReadonly}
            />
          </Suspense>
        );
      }

      // A `none_type` input can only ever be None, so there is nothing to edit.
      case 'none':
        return (
          <Text level="small" color="muted" data-testid={`input-${spec.name}`}>
            {t('labels.noValueNeeded')}
          </Text>
        );

      case 'complex':
        return (
          <Suspense fallback={null}>
            <ComplexInput
              name={spec.name}
              value={displayValue as string}
              typeHint={getComplexTypeHint(baseType)}
              onChange={val => handleChange(spec.name, val, 'complex')}
              error={error}
              required={required}
              disabled={isReadonly}
            />
          </Suspense>
        );

      default:
        return renderLeafEditor({
          name: spec.name,
          componentType,
          type: baseType,
          value: displayValue,
          onChange: val => handleChange(spec.name, val, componentType),
          error,
          required,
          label: spec.name,
        });
    }
  };

  return (
    <div className="flex flex-col gap-5 p-4">
      {/* Table Header */}
      <div className="flex gap-5 items-start">
        <Text level="small" color="muted" className="w-[200px] shrink-0">
          {t('labels.whatsNeeded')}
        </Text>
        <Text level="small" color="muted">
          {t('labels.provideInformation')}
        </Text>
      </div>

      {/* Input Rows */}
      {inputSpecs.map(spec => {
        const processed = processInputSpec(spec, values, errors);
        const { error, isOptional } = processed;

        const isDefault = defaultInputKeys.includes(spec.name);

        return (
          <Flex
            key={spec.name}
            direction="row"
            gap={5}
            align="start"
            className="rounded-md"
          >
            {/* Left column - What's Needed */}
            <Flex
              className="w-[200px] shrink-0 break-words"
              gap={2}
              align="start"
            >
              <Flex direction="column" gap={1} className="flex-1 min-w-0">
                <Text level="small" weight="medium">
                  {spec.name}
                </Text>
                {/* Only optionality is called out — most inputs are required,
                    so marking the exception keeps the panel quiet. */}
                {isOptional && (
                  <Text
                    level="small"
                    color="muted"
                    data-testid={`input-optional-${spec.name}`}
                  >
                    {t('labels.optional')}
                  </Text>
                )}
                {isDefault && (
                  <Badge variant="secondary">{t('labels.default')}</Badge>
                )}
              </Flex>
              {!isReadonly && onToggleDefault && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-5 shrink-0"
                    >
                      <Icon type="EllipsisVertical" className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[128px]">
                    <DropdownMenuItem
                      onClick={() => onToggleDefault(spec.name)}
                    >
                      {isDefault
                        ? t('actions.removeDefault')
                        : t('actions.setAsDefault')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </Flex>

            {/* Right column - Provide Information */}
            <Flex direction="column" gap={2.5} className="flex-1 w-full">
              {renderInputComponent(spec, processed, isDefault)}
              {error && (
                <Text level="small" color="destructive">
                  {error}
                </Text>
              )}
            </Flex>
          </Flex>
        );
      })}
    </div>
  );
}
